// Utility functions for authentication operations
import { AUTH_MESSAGES } from '@rsmaids/shared/constants/auth';

export interface PasskeyCreationOptions {
  email: string;
  firstName?: string;
  lastName?: string;
  displayName?: string;
}

export interface PasskeyAuthenticationOptions {
  email: string;
  allowedCredentials?: PublicKeyCredentialDescriptor[];
}

export interface WebAuthnCredentialResult {
  credential: PublicKeyCredential | null;
  success: boolean;
  error?: string;
}

// Check WebAuthn support
export const checkWebAuthnSupport = (): { supported: boolean; message?: string } => {
  if (!window.PublicKeyCredential) {
    return {
      supported: false,
      message: 'Passkeys are not supported on this device. Please use a modern browser.'
    };
  }
  return { supported: true };
};

// Create a new passkey credential
export const createPasskeyCredential = async (
  options: PasskeyCreationOptions
): Promise<WebAuthnCredentialResult> => {
  try {
    const { email, firstName = '', lastName = '', displayName } = options;
    
    const credential = await navigator.credentials.create({
      publicKey: {
        challenge: crypto.getRandomValues(new Uint8Array(32)),
        rp: {
          name: "R&S Maids Service",
          id: window.location.hostname
        },
        user: {
          id: new TextEncoder().encode(email),
          name: email,
          displayName: displayName || `${firstName} ${lastName}`.trim() || email
        },
        pubKeyCredParams: [
          { alg: -7, type: "public-key" }, // ES256
          { alg: -257, type: "public-key" } // RS256
        ],
        authenticatorSelection: {
          userVerification: "preferred"
        },
        timeout: 60000
      }
    }) as PublicKeyCredential | null;

    if (!credential) {
      return {
        credential: null,
        success: false,
        error: 'Passkey creation was cancelled.'
      };
    }

    return {
      credential,
      success: true
    };
  } catch (error) {
    return {
      credential: null,
      success: false,
      error: error instanceof Error ? error.message : 'Passkey creation failed'
    };
  }
};

// Authenticate using existing passkey
export const authenticateWithPasskey = async (
  options: PasskeyAuthenticationOptions = { email: '' }
): Promise<WebAuthnCredentialResult> => {
  try {
    const { allowedCredentials = [] } = options;
    
    const credential = await navigator.credentials.get({
      publicKey: {
        challenge: crypto.getRandomValues(new Uint8Array(32)),
        allowCredentials: allowedCredentials, // Allow any registered passkey if empty
        userVerification: 'preferred',
        timeout: 60000,
        rpId: window.location.hostname
      }
    }) as PublicKeyCredential | null;

    if (!credential) {
      return {
        credential: null,
        success: false,
        error: 'Passkey authentication was cancelled or no passkey found.'
      };
    }

    return {
      credential,
      success: true
    };
  } catch (error) {
    return {
      credential: null,
      success: false,
      error: error instanceof Error ? error.message : 'Passkey authentication failed'
    };
  }
};

// Store passkey association (in production, this would be sent to backend)
export const storePasskeyAssociation = (email: string, credentialId: string): void => {
  try {
    localStorage.setItem(`passkey_${email}`, credentialId);
  } catch (error) {
    console.warn('Failed to store passkey association:', error);
  }
};

// Get stored passkey for email (in production, this would come from backend)
export const getStoredPasskey = (email: string): string | null => {
  try {
    return localStorage.getItem(`passkey_${email}`);
  } catch (error) {
    console.warn('Failed to retrieve passkey association:', error);
    return null;
  }
};

// Whether THIS email has used a passkey on this device. Kept per email: one device often holds several accounts, and a
// single yes/no for the whole device hid "create a passkey" for every account after the first one had used one.
const passkeyUsedKey = (email: string): string => `hasUsedPasskey:${email.trim().toLowerCase()}`;

export const hasUsedPasskeyOnDevice = (email: string): boolean => {
  if (!email.trim()) return false; // nothing typed yet: the "create one" link stays available
  try {
    return localStorage.getItem(passkeyUsedKey(email)) === 'true';
  } catch {
    return false;
  }
};

export const markPasskeyUsedOnDevice = (email: string): void => {
  if (!email.trim()) return;
  try {
    localStorage.setItem(passkeyUsedKey(email), 'true');
  } catch (error) {
    console.warn('Failed to remember that this email used a passkey:', error);
  }
};

// Validate passkey setup requirements
export const validatePasskeySetupRequirements = (
  email: string
): { valid: boolean; message?: string } => {
  if (!email || !email.trim()) {
    return {
      valid: false,
      message: AUTH_MESSAGES.PASSKEY_SETUP_ENTER_EMAIL
    };
  }
  return { valid: true };
};

// Validate passkey login requirements
export const validatePasskeyLoginRequirements = (
  email: string
): { valid: boolean; message?: string } => {
  if (!email || !email.trim()) {
    return {
      valid: false,
      message: AUTH_MESSAGES.ENTER_EMAIL_ADDRESS_FIRST
    };
  }
  return { valid: true };
};
